import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { HELP_API, type Help } from '@chess/api-help';
import { registerCard } from '@chess/api-dashboard';
import '@chess/api-insights/charts.css';
import { LOST_GROUND_CARD, LostGroundCard } from './LostGroundCard.tsx';
import { PRACTISE_CARD, PractiseCard } from './PractiseCard.tsx';
import { LostGroundAggregator } from './LostGroundAggregator.ts';
import { LOST_GROUND_HELP, lostGroundHelp } from './help.tsx';

// The middlegame's causes of lost ground, as two cards over one aggregator:
// "Moves to practise" (`PractiseCard`, rows in games with links; 2026-10-08)
// second among the Middlegame section's top cards (`home.section.middlegame`,
// order 10, after Middlegame results and before Middlegame swings), which the
// Overview's Middlegame section and the Middlegame screen both show; and the
// pie "How you lost ground" (`LostGroundCard`, the top card until that day)
// in the Middlegame screen's "More" (`middlegame.more`, order 5). Both
// register the help topic's rule and draw only their bodies (`registerCard`:
// the section draws the frame); rows and slices open the library filtered
// to those games (`libraryLink`, last 30 days).
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const aggregator = new LostGroundAggregator();
  registerAggregator(getApi, aggregator);
  void app.getApi<Help>(HELP_API).then((help) => help.register(LOST_GROUND_HELP, lostGroundHelp));
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
    // Never hidden while the store loads: the card shows skeletons for its values.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <LostGroundCard loading />;
    return result ? <LostGroundCard result={result} link={link} /> : null;
  };
  const Practise: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <PractiseCard loading />;
    return result ? <PractiseCard result={result} link={link} /> : null;
  };
  registerCard(app, 'home.section.middlegame', Practise, { order: 10, ...PRACTISE_CARD });
  registerCard(app, 'middlegame.more', Card, { order: 5, ...LOST_GROUND_CARD });
}
